import { Link } from "react-router-dom";
import { faShareNodes } from "@fortawesome/free-solid-svg-icons";
import { Button, ButtonType } from "components/Button/Button";
import ShareModal from "components/ShareModal/ShareModal";
import { useModal } from "components/Modal/context/ModalContext";
import { ModalButtons } from "components/Modal/ModalButtons";
import "./CourseDetails.scss";

const CourseDetails = ({ course, linkage = false, previewMode = false, draftId = null }) => {
	const { openModal } = useModal();

	const buildCourseShareLink = () => {
		return `/course-view#courseId-${course.Id}`;
	};

	const getCourseImage = () => {
		return course?.ImageUri || course?.Image || "";
	};

	const handleOpenShareModal = ({ shareLink, contentName }) => {
		if (previewMode) {
			openModal("Preview Mode", "Share unavailable while previewing a course.", ModalButtons.Ok);
		}
		else {
			openModal("Share", <ShareModal shareLink={shareLink} contentName={contentName} />, ModalButtons.None);
		}
	};

	const hasCourseContent = () => {
		if (Array.isArray(course?.SubContent)) {
			return course.SubContent.some((value) => {
				return (value?.ContentType?.length ?? 0) > 0;
			});
		}

		return false;
	};

	const getContinueButtonText = () => {
		return course?.LastViewed?.Continuing ? "Continue" : "Start";
	};

	const getLastViewedLabel = () => {
		return course?.LastViewed?.Continuing ? "Last Viewed" : "Get Started";
	};

	const getLastViewedText = () => {
		return course?.LastViewed?.SubContentName || "--/--/--";
	};

	const buildPreviewHash = (moduleId, submoduleId) => {
		const base = `courseId-${course.Id}-moduleId-${moduleId}-submoduleId-${submoduleId}`;
		return draftId ? `${base}-draftId-${draftId}` : base;
	};

	const getPlayerLink = () => {
		const courseId = course?.Id;
		const moduleId = previewMode ? course?.SubContent[0]?.Id : course?.LastViewed?.ContentId;
		const submoduleId = previewMode ? course?.SubContent[0]?.SubContent[0]?.Id : course?.LastViewed?.SubContentId;

		if (!courseId || !moduleId || !submoduleId) {
			return null;
		}

		if (previewMode) {
			return `/player-preview#${buildPreviewHash(moduleId, submoduleId)}`;
		}

		return `/player#courseId-${courseId}-moduleId-${moduleId}-submoduleId-${submoduleId}`;
	};

	const getContinueSection = () => {
		if (!hasCourseContent()) {
			return null;
		}

		const playerLink = getPlayerLink();

		if (!playerLink) {
			return null;
		}

		if (previewMode) {
			return (
				<div
					className="list-view-item-continue one-button"
					data-test-class="course-details-continue-section"
				>
					<p className="list-view-item-continue-lastviewed" data-test-class="course-details-last-viewed">
						<strong>Get Started:</strong>{" "}
						{course?.SubContent[0].Name}
					</p>

					<Button
						text="Start"
						to={playerLink}
						glint={true}
						data-test-class="course-details-play-button"
					/>
				</div>
			);
		}

		return (
			<div
				className={`list-view-item-continue ${linkage ? "" : "one-button"}`}
				data-test-class="course-details-continue-section"
			>
				<p className="list-view-item-continue-lastviewed" data-test-class="course-details-last-viewed">
					<strong>{getLastViewedLabel()}:</strong>{" "}
					{getLastViewedText()}
				</p>

				{linkage && (
					<Button
						to={buildCourseShareLink()}
						type={ButtonType.Tertiary}
						text="View Full Course"
						scroll={true}
						data-test-class="course-details-view-button"
					/>
				)}

				<Button
					text={getContinueButtonText()}
					to={playerLink}
					glint={!course?.LastViewed?.Continuing}
					customClass={`${linkage ? "button-end" : ""}`}
					data-test-class="course-details-play-button"
				/>
			</div>
		);
	};

	const renderTitle = () => {
		if (linkage && !previewMode) {
			return (
				<Link to={buildCourseShareLink()}>
					<h2 data-test-class="course-details-name-link">{course.Name}</h2>
				</Link>
			);
		}

		return <h2 data-test-class="course-details-name">{course.Name}</h2>;
	};

	const renderShareButton = () => {
		return (
			<div className="list-view-item-details-links">
				<Button
					type={ButtonType.Tertiary}
					text="Share"
					icon={faShareNodes}
					onClick={() =>
						handleOpenShareModal({
							shareLink: buildCourseShareLink(),
							contentName: course.Name
						})
					}
					data-test-class="course-details-share-button"
				/>
			</div>
		);
	};

	const renderImage = () => {
		const imageSrc = getCourseImage();

		if (!imageSrc) {
			return null;
		}

		if (linkage && !previewMode) {
			return (
				<Link to={buildCourseShareLink()}>
					<img
						alt={course.Name}
						src={imageSrc}
						data-test-class="course-details-image-link"
					/>
				</Link>
			);
		}

		return (
			<img
				alt={course.Name}
				src={imageSrc}
				data-test-class="course-details-image"
			/>
		);
	};

	return (
		<div className={`list-view-item-details ${previewMode ? "course-details-preview" : ""}`}>
			<div className="list-view-item-details-text">
				{renderTitle()}

				<p
					className="list-view-item-details-text-description"
					data-test-class="course-details-description"
				>
					{course.Description}
				</p>

				{getContinueSection()}
			</div>

			<div className="list-view-item-details-image">
				{renderShareButton()}
				{renderImage()}
			</div>
		</div>
	);
};

export default CourseDetails;